import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Todo } from '@/infra/types/domain';
import {
  scheduleCompleteRequested,
  cancelScheduledCompleteRequested,
  updateTodo,
  unblockTodoRequested,
} from './ducks';

// Client-side undo window. The server workflow sleeps 1s longer (6s) — the
// gap is a race buffer so cancels fired right at the edge still land before
// the workflow fires.
const UNDO_DELAY_MS = 5000;

export interface ScheduleCompletion {
  /** Checkbox checked state: done, or optimistically completing. */
  isChecked: boolean;
  /** t < 5s: durable workflow started, inside the client undo window. */
  pendingComplete: boolean;
  /** 5s <= t < 6s: client timer expired, workflow not yet confirmed. */
  optimisticDone: boolean;
  /** aria-live status string for the most recent transition. */
  announcement: string;
  /** Toggle handler for the checkbox onClick (stops row-click propagation). */
  toggle: (e: { stopPropagation?: () => void }) => void;
  /** Keyboard handler mirroring toggle on Enter / Space. */
  onKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Encapsulates the schedule-complete state machine shared by the Todos-page
 * checkbox (TodoItem) and the Projects inbox leaf-row checkbox
 * (InboxProjectRow): a 5s client undo window over the server's 6s durable
 * completion workflow, with optimistic-done bridging the race, unmount
 * finalization, and the blocked-completion unblock path.
 *
 * `onUpdate` doubles as the read-only gate — when undefined, the checkbox is
 * inert (partner rows / non-mutating views).
 */
export function useScheduleCompletion(
  todo: Pick<Todo, 'id' | 'status' | 'close_reason'>,
  onUpdate: ((update: { id: string; [key: string]: unknown }) => void) | undefined,
): ScheduleCompletion {
  const dispatch = useDispatch();
  const [announcement, setAnnouncement] = useState('');
  const [pendingComplete, setPendingComplete] = useState(false);
  // optimisticDone: between t=5s (client timer expired) and t=6s (workflow
  // fires + WS lands). Checkbox stays interactive so a late click still
  // cancels; WS arrival clears this flag via the todo.status effect.
  const [optimisticDone, setOptimisticDone] = useState(false);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Refs mirror the in-limbo flags so the unmount cleanup can read the current
  // value without re-running every time they toggle.
  const pendingCompleteRef = useRef(false);
  const optimisticDoneRef = useRef(false);
  const todoIdRef = useRef(todo.id);

  const isDone = todo.status === 'done';

  useEffect(() => { pendingCompleteRef.current = pendingComplete; }, [pendingComplete]);
  useEffect(() => { optimisticDoneRef.current = optimisticDone; }, [optimisticDone]);
  useEffect(() => { todoIdRef.current = todo.id; }, [todo.id]);

  useEffect(() => {
    return () => {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      // If we unmount while a completion is in-flight (tab switch, navigation,
      // list re-filter), the server workflow still fires at t=6s. Finalize the
      // store optimistically so the next render doesn't flicker back to pending
      // before the WS catches up. completed_at drives the Completed-today
      // window selector; the server WS broadcast overwrites it with the real
      // timestamp once the workflow fires.
      if (pendingCompleteRef.current || optimisticDoneRef.current) {
        dispatch(updateTodo(todoIdRef.current, {
          status: 'done',
          close_reason: 'completed',
          completed_at: new Date().toISOString(),
        }));
      }
    };
  }, [dispatch]);

  // Any server-truth status transition clears the optimistic flag so the
  // checkbox reflects reality — whether the workflow fired (status=done) or
  // the cancel won the race (status stays pending).
  useEffect(() => {
    setOptimisticDone(false);
  }, [todo.status]);

  const toggle = useCallback((e: { stopPropagation?: () => void }) => {
    e.stopPropagation?.();
    if (!onUpdate) return;
    if (pendingComplete) {
      // t < 5s: inside cancel window.
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setPendingComplete(false);
      dispatch(cancelScheduledCompleteRequested(todo.id));
      setAnnouncement('Completion undone');
      return;
    }
    if (optimisticDone) {
      // 5s <= t < 6s: race window. Best-effort cancel — if it races and loses,
      // WS re-flips the checkbox to done via todo.status.
      setOptimisticDone(false);
      dispatch(cancelScheduledCompleteRequested(todo.id));
      setAnnouncement('Completion undone');
      return;
    }
    if (isDone) {
      // Uncomplete-via-checkbox. A blocked completion is special: the server
      // unblock orchestration also soft-deletes the auto-created follow-up +
      // tombstones the completion log row, and unblockTodoSaga ships the full
      // delta so the local store mirrors every server-side mutation.
      if (todo.close_reason === 'blocked') {
        dispatch(unblockTodoRequested(todo.id));
      } else {
        onUpdate({ id: todo.id, status: 'pending', close_reason: null });
      }
      setAnnouncement('Marked as pending');
      return;
    }
    // Fresh check: start the durable workflow immediately, begin local timer.
    setPendingComplete(true);
    dispatch(scheduleCompleteRequested(todo.id));
    setAnnouncement('Completing — uncheck to cancel');
    undoTimerRef.current = setTimeout(() => {
      setPendingComplete(false);
      setOptimisticDone(true);
    }, UNDO_DELAY_MS);
  }, [isDone, pendingComplete, optimisticDone, onUpdate, todo.id, todo.close_reason, dispatch]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(e);
    }
  }, [toggle]);

  return {
    isChecked: isDone || pendingComplete || optimisticDone,
    pendingComplete,
    optimisticDone,
    announcement,
    toggle,
    onKeyDown,
  };
}
